import React from 'react';
import { ExternalLink } from '../router';
import { CONTACT_EMAIL } from '../links';

const Privacy: React.FC = () => (
  <>
    <section className="section page-hero">
      <div className="container container--narrow center">
        <h1>Privacy Policy</h1>
        <p className="subtitle">Last updated: 19 October 2026</p>
      </div>
    </section>

    <section className="section">
      <div className="container container--narrow privacy">
        <p>
          This policy explains what personal data I collect through hannakinez.com, why, and what rights you have. I keep it
          to what I need to answer you, send the KinezioLetter you asked for, and understand how the site is used.
        </p>

        <h2>Who is responsible for your data</h2>
        <p>
          The data controller is <strong>Hanna Kinez (KinezioSomatic)</strong>, based in Poland. For anything about your data,
          write to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>What I collect and why</h2>
        <p>
          The forms on this site (guides, KinezioLetter, enquiries and messages) are provided by <strong>HighLevel</strong>, the
          platform I use to manage contacts and send emails. When you submit one, your details are stored there with a note
          of which form you used, so I can send you what you asked for.
        </p>

        <h3>When you request a guide or join the KinezioLetter</h3>
        <p>
          You give me your <strong>name and email address</strong>. I use them to send the guides you requested, the
          KinezioLetter every two weeks, and news about my workshops and programs. The legal basis is your consent (Art.
          6(1)(a) GDPR). My emails may record whether they were opened or a link was clicked, so I can see what is useful.
          Every email has an unsubscribe link, and you can withdraw your consent at any time.
        </p>

        <h3>When you send an enquiry or a message</h3>
        <p>
          The forms ask for your <strong>name, email address, phone number, company and message</strong>. I use them to reply
          to you and, if you want, to prepare a proposal. The legal basis is the steps you ask me to take before a possible
          agreement, and my legitimate interest in answering you (Art. 6(1)(b) and (f) GDPR).
        </p>
        <p>
          Please don't send medical records or other sensitive health information through the forms. If we work together, we
          agree how to share that safely.
        </p>

        <h3>When you register for the monthly workshop</h3>
        <p>
          You give me your <strong>name, email address and your biggest challenge right now</strong>. I use them to send you
          the registration link, and to prepare the live Q&amp;A. The workshop itself runs on <strong>webinar.gg</strong>,
          which handles your seat, the join link and reminders under its own privacy policy.
        </p>

        <h3>When you book a session</h3>
        <p>
          Booking links open <strong>TidyCal</strong>, a separate scheduling service. The details you enter there are handled
          under TidyCal's own privacy policy and used by me to hold the session.
        </p>

        <h3>Cookies and analytics</h3>
        <p>
          If you click <strong>Accept</strong> on the cookie banner, the site uses <strong>Google Analytics</strong> to
          measure visits: pages viewed, approximate location, device and browser. If you decline, Analytics is not loaded.
          You can change your choice by clearing this site's data in your browser. The forms include bot protection, which
          may use technical cookies to tell people from automated spam. The site is hosted on{' '}
          <strong>Microsoft Azure</strong>, which keeps standard technical server logs for security.
        </p>

        <h2>Who receives your data</h2>
        <p>
          I do not sell your data or share it for anyone else's marketing. It is processed on my behalf only by the services
          named above: HighLevel (forms, contacts and emails), webinar.gg (live workshops), TidyCal (bookings), Google
          (analytics, only with your consent) and Microsoft (hosting). Some of them process data outside the European Economic
          Area, mainly in the United States. Where they do, they rely on safeguards approved under GDPR, such as the EU
          Standard Contractual Clauses.
        </p>

        <h2>How long I keep it</h2>
        <ul className="list">
          <li>Enquiries and messages: as long as needed to answer you and follow up, and no longer than 2 years after our last exchange.</li>
          <li>Guides and KinezioLetter: until you unsubscribe.</li>
          <li>Workshop registrations: until the workshop has taken place, unless you also joined the KinezioLetter.</li>
          <li>Analytics: according to Google Analytics retention settings, at most 14 months.</li>
        </ul>

        <h2>Your rights</h2>
        <p>Under GDPR you have the right to:</p>
        <ul className="list">
          <li>access the data I hold about you and get a copy of it;</li>
          <li>have it corrected or deleted;</li>
          <li>restrict or object to how I use it;</li>
          <li>receive it in a portable format;</li>
          <li>withdraw your consent at any time, without affecting what was done before.</li>
        </ul>
        <p>
          To use any of these rights, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. You can also complain to
          the Polish data protection authority,{' '}
          <ExternalLink href="https://uodo.gov.pl/en">Prezes Urzędu Ochrony Danych Osobowych (UODO)</ExternalLink>, or to the
          authority in your own country.
        </p>

        <h2>External links</h2>
        <p>
          The site links to YouTube, LinkedIn, Instagram and my other program pages. Those sites have their own privacy
          policies, which apply once you leave hannakinez.com.
        </p>

        <h2>Changes to this policy</h2>
        <p>If I change how I handle your data, I will update this page and the date at the top.</p>
      </div>
    </section>
  </>
);

export default Privacy;
